import type { IFuseOptions } from 'fuse.js';
import type { ClinicalTimelineSearchIndexRecord } from './clinical-timeline-search-types';

/**
 * Fuse index options shared by the worker and main-thread fallback.
 *
 * `ignoreLocation: true` (previous default) lets every query character match anywhere in long
 * clinical notes, which produces scattered single-letter highlights. We disable that and use a
 * large `distance` so matches still work mid–long-form body text.
 */
export function createTimelineFuseOptions(): IFuseOptions<ClinicalTimelineSearchIndexRecord> {
  return {
    keys: [
      'title',
      'body',
      'footer',
      'heading',
      'dates',
      'diagnosis',
      'summary',
      'hospital',
      'discharge',
      'text'
    ],
    includeMatches: true,
    includeScore: true,
    threshold: 0.24,
    ignoreLocation: false,
    distance: 16000,
    /** Drop 1–2 character Fuse match fragments in the index pass. */
    minMatchCharLength: 3,
    isCaseSensitive: false,
    findAllMatches: false
  };
}

/**
 * Maximum Fuse result `score` to keep (0 = best match, 1 = worst). Tightens as the query
 * grows so longer phrases match fewer, higher-quality rows.
 */
export function maxFuseScoreForQuery(query: string): number {
  const t = query.trim();
  const len = t.length;
  const words = t.split(/\s+/).filter((w) => w.length > 0);
  const wordCount = words.length;

  if (len <= 2) {
    return 0.52;
  }
  if (len <= 4) {
    return 0.44;
  }
  if (len <= 8) {
    return 0.38;
  }
  /** One long token (e.g. "assessment") — require a tight Fuse score, not scattered letter hits. */
  if (wordCount === 1 && len <= 14) {
    return 0.22;
  }
  if (wordCount === 1) {
    return 0.2;
  }
  if (wordCount >= 4) {
    return 0.22;
  }
  if (wordCount >= 3) {
    return 0.26;
  }
  if (len >= 18) {
    return 0.28;
  }
  return 0.3;
}
